import { FC, ReactNode, useMemo, useCallback } from 'react';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useFundContext from '@/contexts/Fund/useFundContext';
import FeatureFlagsContext from './FeatureFlagsContext';

/**
 * Fetches the evaluated feature-flag map for the currently-selected fund + user.
 * The query key includes the fund id, so switching funds refetches automatically.
 * Server remains the enforcement authority; this only drives UI visibility.
 */
const FeatureFlagsProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const { currentFund } = useFundContext();

  const url = currentFund?.id ? `funds/${currentFund.id}/feature-flags` : '';
  const { data, isLoading } = useGetAuthApi<Record<string, boolean>>(url);

  const flags = useMemo(() => data ?? {}, [data]);

  const isEnabled = useCallback(
    (featureKey: string) => flags[featureKey] === true,
    [flags],
  );

  const value = useMemo(
    () => ({ flags, isLoading, isEnabled }),
    [flags, isLoading, isEnabled],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export default FeatureFlagsProvider;
